<!--
  PoolaLastRunPage — /poola/last/ and /suppliers/poola/last/ — «البولة اللي فاتت».

  The last finished Poola, read-only, while the next one counts down. Opened by
  «شوف كل عروضها» on the recap card of the Poola page's countdown screen.

  WHAT IT SHOWS: a slim strip PINNED under the navbar that keeps the countdown
  to the next Poola on screen while the page scrolls, then today's "Poola ended"
  view for the last run (PoolaEndedView — the same fragment PoolaPage renders
  when the current run is ENDED).

  DATA: its own usePoolaDeal(). The backend adds meta.previousRun to
  GET /poola-deals/current only while the current run is SCHEDULED, and the
  composable re-reads it on every fetch — so the moment the next run opens, a
  silent refetch drops the key. The run is therefore KEPT here once seen: the
  member is reading it, and it must not blank mid-read. Only a page that never
  had a run worth showing (utils/poolaLastRun.js — the rule the recap card
  uses) leaves.

  ONE MOUNTED OFFERS LIST AT A TIME (the trap this page depends on):
  usePoolaOffers flushes the whole PoolaOffer table on every full load, and both
  this page and the Poola page behind it mount one. That is safe because
  Framework7 REMOVES the previous page on every push here (App.vue keeps
  preloadPreviousPage and iosSwipeBack off — playbook Section 5 row 18), so the
  Poola page is gone while this one is up and is rebuilt, with a fresh load, on
  the way back; and because a removed page's load is aborted (usePoolaOffers).
  tests/e2e/poola-last-run.spec.js pins the round trip. What is NOT covered is
  the transition itself, a few hundred milliseconds in which both pages are
  mounted (deferred-work.md).

  ROUTE INVARIANTS (playbook Section 5, mirrors TadbeesaPage):
   - EAGER `component:` in routes.js — a reload makes this the boot URL, and
     `asyncComponent:` would paint "[object Promise]" (row 16).
   - requireAuth, like the Poola page; the cold hard-load guard skip (row 22) is
     covered by App.vue's EAGER_AUTH_REQUIRED_PATHS.
   - `meta.hideTabbar: true`; slashless normalizers AFTER the strict routes (row 20).
   - ONE <f7-page>. Everything inside is a content fragment.

  TWO THINGS THE BROWSER CHECK TAUGHT (do not undo either):
   - NO COMMENT AT THE ROOT OF THE TEMPLATE. In dev Vue keeps template comments,
     so a comment next to <f7-page> makes the component a FRAGMENT. On a back
     navigation Framework7 moves the returning page's element in front of this
     one — inside this fragment's range — and when Vue then removes this
     fragment it takes the returning page with it: a blank view. Notes go here,
     above <template>, or inside <f7-page>.
   - `:page-content="false"`. The page brings its own <f7-page-content> (for
     pull-to-refresh); without the prop <f7-page> wraps it in a second
     .page-content, both padded by the navbar's height, and the content starts
     one navbar-height too low. The pinned strip needs it too
     (useFixedSummaryBar).
-->
<template>
  <f7-page
    name="poola-last"
    :page-content="false"
    :style="pageStyleVars"
    @page:beforein="onPageBeforeIn"
    @page:afterin="onPageAfterIn"
  >
    <!-- Framework7's own back link is a `.link.back`, which the router pops
         blindly. This one looks the same (same icon class, same label) but
         asks `leave` first — see "Leaving" below. -->
    <f7-navbar :title="$t('poola.lastRun.title')">
      <template #left>
        <f7-link
          class="poola-last-page__back"
          icon="icon-back"
          :text="$t('general.back')"
          @click="leave"
        ></f7-link>
      </template>
    </f7-navbar>

    <!-- The strip, pinned just under the navbar and OUTSIDE the scroller, so the
         countdown stays on screen while the offers scroll. Same mechanism as the
         supplier order pages' summary bar: absolutely positioned, its measured
         height reserved as page-content top padding (useFixedSummaryBar).
         :key — the strip reads its target once at setup; a start time that
         moves under it (a rescheduled run) gets a fresh strip. -->
    <div v-if="run && showStrip" :ref="setSummaryEl" class="poola-last-page__strip">
      <poola-next-run-strip
        :key="stripKey"
        :deal="deal"
        :server-offset="serverOffset"
        :href="paths.poola"
        @open="goToPoola"
      ></poola-next-run-strip>
    </div>

    <f7-page-content ptr ptr-mousewheel @ptr:refresh="handlePullToRefresh">
      <poola-error v-if="state === POOLA_STATE.ERROR" @retry="refresh"></poola-error>

      <poola-ended-view
        v-else-if="run"
        :key="run.id"
        :deal="run"
        :festival="run.festival"
        :server-offset="serverOffset"
        :viewer-role="viewerRole"
        @deal-not-active="refresh"
      ></poola-ended-view>

      <!-- First load, and the moment before leaving when there is no run to show. -->
      <poola-skeleton v-else></poola-skeleton>
    </f7-page-content>
  </f7-page>
</template>

<script>
// Module-level, on purpose: how THIS page's history entry was made — by an in-app
// push, or by a boot / reload / replace. It has to outlive the component: when
// something is pushed from this page (a visitor's «انضم واحجز» opens /join/) and
// the member comes back, Framework7 REBUILDS this page, and that arrival
// (`from: 'previous'`) says nothing about the entry it returned to. A reload
// starts the module again, which is exactly the reset a reload needs.
let entryPushedInApp = false;
</script>

<script setup>
import { computed, ref, shallowRef, watch } from 'vue';
import PoolaEndedView from '@/components/poola/PoolaEndedView.vue';
import PoolaError from '@/components/poola/PoolaError.vue';
import PoolaNextRunStrip from '@/components/poola/PoolaNextRunStrip.vue';
import PoolaSkeleton from '@/components/poola/PoolaSkeleton.vue';
import { useFixedSummaryBar } from '@/composables/useFixedSummaryBar.js';
import { POOLA_STATE, usePoolaDeal } from '@/composables/usePoolaDeal.js';
import { lastRunWorthShowing } from '@/utils/poolaLastRun.js';
import { poolaPathsFor } from '@/utils/poolaPaths.js';

// F7-Vue passes f7route / f7router as props to `component:` routes.
const props = defineProps({
  f7route: { type: Object, default: null },
  f7router: { type: Object, default: null },
});

const { deal, state, refresh, serverOffset, viewerRole, previousRun } = usePoolaDeal();

// The pinned strip: its element is measured and its height reserved above the
// page content (nothing is reserved while the strip is not rendered).
const { setSummaryEl, pageStyleVars } = useFixedSummaryBar();

// The Poola page this one belongs to: /suppliers/poola/ under /suppliers/,
// otherwise /poola/.
const paths = computed(() => poolaPathsFor(props.f7route?.path ?? window.location.pathname));

// ── The run on screen ────────────────────────────────────────────────────────
// The freshest previousRun while the backend sends one worth showing, otherwise
// the last one seen (see DATA above). A computed, not a watcher-fed ref:
// `runMissing` below must never read a stale null in the tick the first fetch
// lands.
const kept = shallowRef(null);
const run = computed(() => lastRunWorthShowing(previousRun?.value) ?? kept.value);
watch(
  run,
  (next) => {
    if (next) kept.value = next;
  },
  { immediate: true },
);

const loaded = computed(
  () => state.value !== POOLA_STATE.LOADING && state.value !== POOLA_STATE.ERROR,
);
const runMissing = computed(() => loaded.value && run.value == null);

// ── The strip ────────────────────────────────────────────────────────────────
// While the next run is SCHEDULED and has a start time: the countdown. Once it
// flips to ACTIVE under a member who is still reading: its "open now" link.
// Nothing otherwise (no start time to count to, or no next run any more).
const hasStartAt = computed(() => {
  const raw = deal.value?.startAt;
  if (raw == null) return false;
  return Number.isFinite(typeof raw === 'number' ? raw : Date.parse(raw));
});
const showStrip = computed(
  () =>
    state.value === POOLA_STATE.LIVE || (state.value === POOLA_STATE.COMING && hasStartAt.value),
);
const stripKey = computed(() => `${deal.value?.id ?? ''}:${deal.value?.startAt ?? ''}`);

// PTR → full singleton refetch (usePoolaDeal.refresh resolves `done` itself).
function handlePullToRefresh(done) {
  refresh(done);
}

// ── Leaving ──────────────────────────────────────────────────────────────────
//
// router.back() is only safe when this entry was made by an in-app push. On a
// boot or reload of this URL Framework7's history is made up
// (['/', '/poola/last/']) or restored from an earlier visit, and back() also
// calls the browser's History.back(), which leaves the app when nothing real is
// behind this entry. F7 reports how the page arrived (navigate.js / back.js):
//   'next'     a forward push          → the entry is ours, back() is safe
//   'previous' a back from a page that was pushed from here → the page was
//              rebuilt on the SAME entry: keep what we knew about it
//   'current'  a boot, a reload, or a replace → nothing trustworthy behind
// Same gate as TadbeesaPage, plus the memory across rebuilds.
//
// `animatingIn` is true between the two events of an animated arrival. Nothing
// here navigates in that window: the page this one is replacing is still the
// router's current page, and Framework7 does NOT hold a navigation back for us
// (a route with a beforeEnter guard re-opens allowPageChange as soon as the
// guard resolves — process-route-queue.js). A back() started then runs against
// a half-finished transition.
let animatingIn = false;
const pageIn = ref(false);

// Set the moment this page asks the router to leave. Nothing navigates twice:
// a second tap, or the "no run to show" exit firing while a back is already
// under way, would run against the transition (by then the router has popped
// its history, so the page "behind" is no longer the one it was).
let leaving = false;

function onPageBeforeIn(page) {
  const from = page?.from;
  if (from === 'next') entryPushedInApp = true;
  else if (from !== 'previous') entryPushedInApp = false;
  animatingIn = from === 'next' || from === 'previous';
}

function onPageAfterIn() {
  animatingIn = false;
  pageIn.value = true;
}

function pathOf(url) {
  return typeof url === 'string' ? url.split('?')[0] : null;
}

/** The path of the page right behind this one in the router's history. */
function pathBehind(router) {
  return pathOf(router?.history?.[router.history.length - 2]);
}

/**
 * This entry BECOMES the Poola page (no push), which is what a cold load of the
 * Poola page itself leaves behind. TadbeesaPage pushes home and clears the
 * history instead; that would not do here, because the Poola page has its own
 * back link and it needs an entry behind it — with a cleared history that link
 * does nothing at all.
 */
function replaceWithPoola() {
  const router = props.f7router;
  if (!router) return;
  // A history restored after a reload already holds the Poola page right behind
  // this entry. Replacing this one too would leave the same URL twice in a row
  // in the ROUTER's history, and Framework7 refuses to go "back" to the URL it
  // is on — the Poola page's back link would then never work. Drop the older
  // copy first, unless it is the only thing behind us. The BROWSER still holds
  // two /poola/ entries after this, so that link bounces once before it works
  // (deferred-work.md: the Poola page uses Framework7's plain back link).
  // propsHistory runs parallel to history but is not restored after a reload,
  // so its splice does nothing in exactly that case.
  const behind = router.history.length - 2;
  if (behind > 0 && pathBehind(router) === paths.value.poola) {
    router.history.splice(behind, 1);
    router.propsHistory?.splice(behind, 1);
  }
  router.navigate(paths.value.poola, { reloadCurrent: true });
}

/** The back link: back to the page that pushed this one; the Poola page when
 *  there is no such page (a boot or reload). */
function leave() {
  const router = props.f7router;
  if (!router || animatingIn || leaving) return;
  leaving = true;
  if (entryPushedInApp && router.history.length > 1) router.back();
  else replaceWithPoola();
}

/** To the Poola page, whatever is behind this one: a back when the Poola page
 *  IS right behind it, otherwise in place of this entry (never on top of it —
 *  Poola → last → Poola would make every later «رجوع» bounce between the two). */
function goToPoola() {
  const router = props.f7router;
  if (!router || animatingIn || leaving) return;
  leaving = true;
  if (entryPushedInApp && pathBehind(router) === paths.value.poola) router.back();
  else replaceWithPoola();
}

// Loaded, and there is no last run to show (nothing is scheduled, the viewer is
// not eligible, or the run has nothing in it): this page has nothing to say.
// Waits for the page to be in (see animatingIn), and does nothing once the page
// is already leaving (see `leaving`).
watch(
  [runMissing, pageIn],
  ([missing, settled]) => {
    if (missing && settled) goToPoola();
  },
  { immediate: true },
);
</script>

<style scoped>
/* Same soft gray as the Poola page it was pushed from. */
:deep(.page-content) {
  background: var(--poolit-bg-soft);
}

/* Pinned under the navbar, outside the scroller (see useFixedSummaryBar; same
   box as the supplier order pages' summary bar). */
.poola-last-page__strip {
  position: absolute;
  top: var(--f7-page-navbar-offset, 0px);
  right: 0;
  left: 0;
  z-index: 20;
}
</style>
